import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import ValidationError from 'domain/errors/ValidationError';
import createRecipe from 'domain/recipe/usecases/create_recipe';
import useAuthentication from 'domain/authentication/useAuthentication';

import delayed from 'utils/delayed';

const useCreateRecipeRequest = (recipe, idempotencyToken) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState('');
  const [isRecipeInvalid, setIsRecipeInvalid] = useState(false);
  const [wasRecipeCreated, setWasRecipeCreated] = useState(false);
  const { user } = useAuthentication();

  useEffect(() => {
    if (!isLoading) return;

    setHasFailed(false);

    const postData = async () => {
      try {
        await delayed(createRecipe(recipe, idempotencyToken, user));

        setIsLoading(false);
        enqueueSnackbar('Recipe was created successfully', {
          variant: 'success',
        });
        setWasRecipeCreated(true);
      } catch (error) {
        setIsLoading(false);

        if (error instanceof ValidationError) {
          setIsRecipeInvalid(true);
          return;
        }

        enqueueSnackbar('Recipe creation failed', {
          variant: 'error',
        });

        setHasFailed(true);
        throw error;
      }
    };
    postData();
  }, [recipe, isLoading, enqueueSnackbar, idempotencyToken, user]);

  const retryRequest = () => setIsLoading(true);

  return {
    isLoading,
    retryRequest,
    hasFailed,
    isRecipeInvalid,
    wasRecipeCreated,
  };
};

export default useCreateRecipeRequest;
